import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import type {
  ContentDetailsParams,
  SearchResultItem,
} from '@bizzdesign/data-access-search';
import {
  buildViewMenuOptions,
  buildWorkspaceSearchMenuOptions,
  sortViewItems,
  sortWorkspaceItems,
  toViewListItem,
  toWorkspaceListItem,
  type ViewItem,
  type ViewMenuHandlers,
  type WorkspaceItem,
  type WorkspaceSearchMenuHandlers,
  type WorkspacesListItem,
} from '@bizzdesign/data-access-workspaces';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { type Item } from '@bizzdesign/shared/models';
import { BadgeComponent } from '@bizzdesign/ui-badge';
import {
  ItemGridComponent,
  ItemGridLoadingComponent,
} from '@bizzdesign/ui-item-grid';
import {
  lastEditedOption,
  lastOpenedOption,
} from '@bizzdesign/workspaces/utils';
import { ButtonModule } from 'primeng/button';
import { ObjectSearchResultItemComponent } from '../object-search-result-item/object-search-result-item.component';
import { ObjectSearchResultsLoadingComponent } from '../object-search-results/object-search-results-loading.component';
import {
  OBJECT_RESULTS_SYSTEM_COLUMN_CATALOGUE,
  type ObjectResultsColumnCatalogue,
} from '../../utils/object-results-columns.utils';
import {
  ObjectSearchResultsTableComponent,
  type ManageWorkspaceAccess,
  type ObjectResultsTableLayout,
  type ObjectResultsTableSelection,
} from '../object-search-results-table/object-search-results-table.component';

@Component({
  selector: 'bd-all-search-results',
  imports: [
    NgTemplateOutlet,
    SharedI18nModule,
    BadgeComponent,
    ButtonModule,
    ItemGridComponent,
    ItemGridLoadingComponent,
    ObjectSearchResultsTableComponent,
    ObjectSearchResultItemComponent,
    ObjectSearchResultsLoadingComponent,
  ],
  templateUrl: './all-search-results.component.html',
  styleUrl: '../search-results-tabs.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AllSearchResultsComponent {
  viewItems = input<ViewItem[]>([]);
  workspaceItems = input<WorkspaceItem[]>([]);
  objectResultItems = input<SearchResultItem[]>([]);
  viewsCount = input<number>(0);
  workspacesCount = input<number>(0);
  objectsCount = input<number>(0);
  viewsLoading = input<boolean>(false);
  workspacesLoading = input<boolean>(false);
  objectsLoading = input<boolean>(false);
  manageWorkspaceActionDisabled = input<boolean>(false);
  manageWorkspaceNoAccessTooltip = input<string>('');
  objectSelectionMode = input<boolean>(false);
  selectedObjectItems = input<SearchResultItem[]>([]);
  activeItemId = input<string>('');
  isDrawerOpen = input<boolean>(false);
  filtersHeight = input<number>(0);
  objectResultsMode = input<'list' | 'table'>('list');
  columnCatalogue = input<ObjectResultsColumnCatalogue>(
    OBJECT_RESULTS_SYSTEM_COLUMN_CATALOGUE,
  );

  seeAllViews = output<void>();
  seeAllWorkspaces = output<void>();
  seeAllObjects = output<void>();
  openView = output<ViewItem>();
  openViewDetails = output<ViewItem>();
  copyViewToFrame = output<ViewItem>();
  copyViewLink = output<ViewItem>();
  deleteView = output<ViewItem>();
  openWorkspace = output<WorkspaceItem>();
  openWorkspaceDetails = output<WorkspaceItem>();
  inviteToWorkspace = output<WorkspaceItem>();
  copyWorkspaceLink = output<WorkspaceItem>();
  archiveWorkspace = output<WorkspaceItem>();
  unarchiveWorkspace = output<WorkspaceItem>();
  openSearchResultItem = output<SearchResultItem>();
  deleteSearchResultItem = output<SearchResultItem>();
  openContentDetails = output<ContentDetailsParams>();
  objectItemSelectionToggled = output<{
    shiftKey: boolean;
    itemId: SearchResultItem['id'];
  }>();
  objectSelectedItemsChange = output<SearchResultItem[]>();

  readonly tableLayout: ObjectResultsTableLayout = {
    showHeader: false,
    showSelectionColumn: false,
    scrollable: false,
  };
  readonly tableSelection = computed<ObjectResultsTableSelection>(() => ({
    isSelectionMode: this.objectSelectionMode(),
    selectedItems: this.selectedObjectItems(),
    activeItemId: this.isDrawerOpen() ? this.activeItemId() : '',
  }));
  readonly tableManageWorkspaceAccess = computed<ManageWorkspaceAccess>(() => ({
    actionDisabled: this.manageWorkspaceActionDisabled(),
    noAccessTooltip: this.manageWorkspaceNoAccessTooltip(),
  }));

  private readonly menuHandlers: WorkspaceSearchMenuHandlers = {
    onOpen: (workspaceItem) => this.openWorkspace.emit(workspaceItem),
    onSeeDetails: (workspaceItem) =>
      this.openWorkspaceDetails.emit(workspaceItem),
    onInvite: (workspaceItem) => this.inviteToWorkspace.emit(workspaceItem),
    onCopyLink: (workspaceItem) => this.copyWorkspaceLink.emit(workspaceItem),
    onArchive: (workspaceItem) => this.archiveWorkspace.emit(workspaceItem),
    onUnarchive: (workspaceItem) => this.unarchiveWorkspace.emit(workspaceItem),
  };

  private readonly viewMenuHandlers: ViewMenuHandlers = {
    onPreview: (viewItem) => this.openView.emit(viewItem),
    onSeeDetails: (viewItem) => this.openViewDetails.emit(viewItem),
    onCopyToFrame: (viewItem) => this.copyViewToFrame.emit(viewItem),
    onCopyLink: (viewItem) => this.copyViewLink.emit(viewItem),
    onDelete: (viewItem) => this.deleteView.emit(viewItem),
  };
  private readonly previewedItemCount = 3;
  private readonly previewedObjectCount = 5;

  readonly previewedObjectItems = computed(() =>
    this.objectResultItems().slice(0, this.previewedObjectCount),
  );

  readonly viewListItems = computed<Item<ViewItem>[]>(() =>
    sortViewItems(this.viewItems(), lastEditedOption.key)
      .slice(0, this.previewedItemCount)
      .map((viewItem) => ({
        ...toViewListItem(viewItem, {
          actionButton: {
            labelKey: 'workspaces.openView',
            tScope: 'featureWorkspaces',
            styleClass: 'bd-size-small',
            group: 'neutral',
            type: 'asLink',
            onClick: () => this.openView.emit(viewItem),
          },
          onSelect: () => this.openViewDetails.emit(viewItem),
          menuOptions: buildViewMenuOptions(viewItem, this.viewMenuHandlers, {
            includeSeeDetails: true,
          }),
        }),
        // The panel header already names the type; drop the builder's icon.
        icon: undefined,
      })),
  );

  readonly workspaceListItems = computed<WorkspacesListItem[]>(() =>
    sortWorkspaceItems(this.workspaceItems(), lastOpenedOption.key)
      .slice(0, this.previewedItemCount)
      .map((workspaceItem) => ({
        ...toWorkspaceListItem(workspaceItem, {
          sortOption: lastOpenedOption.key,
          frameCount: undefined,
          frameCountLoading: false,
          menuOptions: buildWorkspaceSearchMenuOptions(
            workspaceItem,
            this.menuHandlers,
            { includeSeeDetails: true },
          ),
          actionButton: workspaceItem.archived
            ? undefined
            : {
                labelKey: 'workspaces.openWorkspace',
                tScope: 'featureWorkspaces',
                styleClass: 'bd-size-small',
                group: 'neutral',
                type: 'asLink',
                onClick: () => this.openWorkspace.emit(workspaceItem),
              },
          onSelect: () => this.openWorkspaceDetails.emit(workspaceItem),
        }),
        icon: undefined,
      })),
  );

  readonly onSeeAllViews = () => this.seeAllViews.emit();
  readonly onSeeAllWorkspaces = () => this.seeAllWorkspaces.emit();
  readonly onSeeAllObjects = () => this.seeAllObjects.emit();
}
